import { useEffect, useRef } from "react";
import Lenis from "lenis";

/**
 * Momentum smooth scrolling with Lenis.
 * Respects prefers-reduced-motion (disabled entirely if user prefers less motion).
 * Also disabled on touch devices under 768px to preserve native mobile feel.
 */
export function useLenisScroll() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const isSmallTouch =
      window.matchMedia("(pointer: coarse)").matches &&
      window.innerWidth < 768;

    if (prefersReduced || isSmallTouch) {
      return;
    }

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.2,
    });
    lenisRef.current = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return lenisRef;
}

/**
 * Smooth-scroll to an element by id, respecting Lenis if it's active.
 */
export function scrollToId(id, offset = -72) {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY + offset;
  window.scrollTo({ top: y, behavior: "smooth" });
}
